import * as React from "react"
import { Alert, AlertTitle, Box, Skeleton } from "@mui/material"

import { ActionButton, AppTableSeeker } from "@components"
import { RRSP_DISABLED } from "../@constants"
import { usePricingConfigData, usePricingConfigDraft } from "../@hooks"
import { customerSetup, rrspData, rrspFormFilters } from "../@signals"
import RrspDrawer from "./RrspDrawer"
import { actionsColumn, marketColumn, priceColumn, skuColumn, validityColumn } from "./columns"


/** Branch 3b: the mirror of the `RRSP` table for this customer's catalogue.
 *
 * ⚠️ `RRSP` has no `id_customer`: it is global per SKU × market, and the rows listed here are the
 * ones matching the customer's SKUs. **Two customers that share a SKU share its RRSP**, so editing
 * a row moves the reference price for both — hence the warning above the table.
 *
 * And when the customer has the RRSP feature switched off (`use_rrsp = 0`, which is the case of
 * customer 6 today), the branch is **read-only**: rows can be looked at, but configuring a price the
 * app will not use would be work with no effect.
 */
export default function ManagementPricingConfigRrsp() {

    const { isLoading } = usePricingConfigData()
    const { stageRrspRemoval } = usePricingConfigDraft()

    if (isLoading) return <Skeleton variant="rectangular" height={400} />

    const isRrspDisabled = customerSetup.value?.useRrsp === RRSP_DISABLED

    const columns = [
        skuColumn(),
        marketColumn(),
        validityColumn(),
        priceColumn(),
        // No actions when the feature is off: nothing here can take effect.
        ...isRrspDisabled ? [] : [actionsColumn(stageRrspRemoval)]
    ]

    return <Box display="flex" flexDirection="column" gap={2}>
        {!!isRrspDisabled && <Alert severity="warning" variant="outlined">
            <AlertTitle>This customer does not use the RRSP</AlertTitle>
            Its <code>use_rrsp</code> feature is set to <code>0</code>, so the app drops the RRSP
            columns for it. Nothing can be configured here until that feature is enabled — the rows
            below are shown for reference only.
        </Alert>}

        <Alert severity="info" variant="outlined">
            RRSP rows are stored per SKU and market, not per customer: a SKU shared with another
            customer shares its recommended price.
        </Alert>

        <AppTableSeeker
            data={rrspData.value}
            title="RRSP of the customer's catalogue"
            columns={columns}
            options={{
                viewColumns: false,
                ...!isRrspDisabled && {
                    customToolbar: () => <ActionButton
                        label="Add RRSP row"
                        icon="hexagon-plus"
                        onClick={() => rrspFormFilters.value = {
                            rrspID: null,
                            market: null,
                            dateFrom: null,
                            dateTo: null,
                            skuID: null,
                            skuName: null,
                            price: '',
                            currency: null,
                            isCreateMode: true
                        }}
                    />
                }
            }}
        />

        <RrspDrawer />
    </Box>
}
